import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useQuery } from '@tanstack/react-query';
import {
  ChecklistItemSchema,
  TaskCommentSchema,
  TaskDetailSchema,
  TaskSummarySchema,
  type ProjectMemberDto,
  type ProjectWorkspaceDto,
  type TaskDetailDto,
  type TaskPriority,
  type TaskSummaryDto,
  type UpdateTaskRequest,
} from '@taqon/contracts';
import { z } from 'zod';
import {
  deleteNoContent,
  errorMessage,
  getData,
  patchJson,
  postJson,
} from '../../../shared/api/client';
import { DocumentsPanel } from '../../../shared/documents/DocumentsPanel';
import { useSession } from '../../../shared/session/session';
import { PersonName } from '../../../shared/ui/person';
import { PRIORITY_LABELS, useActivity, useCsrf, useWorkMutation, WORK, workKeys } from './api';
import { DueDate, PriorityChip } from './parts';

/**
 * One task, opened over the board in a centred dialog, full screen on a phone (`?task=ID`; CLR-66): its details, which a member changes and
 * saves at once; its subtasks, checklist and comments with @mentions; its attachments, through
 * document management with their versions; and what has happened to it. A viewer reads it all and
 * may comment.
 */
export function TaskPanel({
  projectId,
  taskId,
  workspace,
  tasks,
  today,
  onClose,
  onOpen,
}: {
  projectId: string;
  taskId: string | null;
  workspace: ProjectWorkspaceDto;
  tasks: readonly TaskSummaryDto[];
  today: string;
  onClose: () => void;
  onOpen: (taskId: string) => void;
}) {
  const detail = useQuery({
    queryKey: workKeys.task(taskId ?? ''),
    enabled: taskId !== null,
    queryFn: () => getData(`${WORK}/tasks/${taskId ?? ''}`, TaskDetailSchema),
  });
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      open={taskId !== null}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={phone}
      scroll="paper"
      aria-labelledby="task-dialog-title"
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography
          id="task-dialog-title"
          variant="subtitle2"
          component="h2"
          color="text.secondary"
          sx={{ flex: 1 }}
        >
          {detail.data?.key ?? 'Task'}
        </Typography>
        <IconButton aria-label="Close the task" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {detail.isPending && taskId !== null && (
          <Typography color="text.secondary">Loading the task…</Typography>
        )}
        {detail.isError && (
          <Alert severity="error" variant="outlined">
            {errorMessage(detail.error)}
          </Alert>
        )}
        {detail.data !== undefined && (
          <TaskBody
            key={`${detail.data.id}:${String(detail.data.version)}`}
            projectId={projectId}
            task={detail.data}
            workspace={workspace}
            tasks={tasks}
            today={today}
            onOpen={onOpen}
            onClose={onClose}
          />
        )}
      </Box>
    </Dialog>
  );
}

interface Draft {
  title: string;
  description: string;
  priority: TaskPriority;
  milestoneId: string;
  startDate: string;
  dueDate: string;
  hours: string;
  minutes: string;
  assigneeIds: string[];
  labelIds: string[];
}

function draftOf(task: TaskDetailDto): Draft {
  const estimate = task.estimateMinutes;
  return {
    title: task.title,
    description: task.description ?? '',
    priority: task.priority,
    milestoneId: task.milestoneId ?? '',
    startDate: task.startDate ?? '',
    dueDate: task.dueDate ?? '',
    hours: estimate === null ? '' : String(Math.floor(estimate / 60)),
    minutes: estimate === null ? '' : String(estimate % 60),
    assigneeIds: [...task.assigneeIds],
    labelIds: [...task.labelIds],
  };
}

/** Only what changed, so a save never re-sends a value someone else may have changed meanwhile. */
export function changesOf(task: TaskDetailDto, draft: Draft): UpdateTaskRequest {
  const changes: UpdateTaskRequest = {};
  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());
  if (draft.title.trim() !== task.title) changes.title = draft.title.trim();
  if (orNull(draft.description) !== task.description)
    changes.description = orNull(draft.description);
  if (draft.priority !== task.priority) changes.priority = draft.priority;
  if (orNull(draft.milestoneId) !== task.milestoneId)
    changes.milestoneId = orNull(draft.milestoneId);
  if (orNull(draft.startDate) !== task.startDate) changes.startDate = orNull(draft.startDate);
  if (orNull(draft.dueDate) !== task.dueDate) changes.dueDate = orNull(draft.dueDate);
  const estimate =
    draft.hours.trim() === '' && draft.minutes.trim() === ''
      ? null
      : (Number(draft.hours || 0) || 0) * 60 + (Number(draft.minutes || 0) || 0);
  if (estimate !== task.estimateMinutes) changes.estimateMinutes = estimate;
  const same = (a: readonly string[], b: readonly string[]) =>
    a.length === b.length && [...a].sort().join() === [...b].sort().join();
  if (!same(draft.assigneeIds, task.assigneeIds)) changes.assigneeIds = draft.assigneeIds;
  if (!same(draft.labelIds, task.labelIds)) changes.labelIds = draft.labelIds;
  return changes;
}

function TaskBody({
  projectId,
  task,
  workspace,
  tasks,
  today,
  onOpen,
  onClose,
}: {
  projectId: string;
  task: TaskDetailDto;
  workspace: ProjectWorkspaceDto;
  tasks: readonly TaskSummaryDto[];
  today: string;
  onOpen: (taskId: string) => void;
  onClose: () => void;
}) {
  const csrf = useCsrf();
  const [draft, setDraft] = useState<Draft>(() => draftOf(task));
  const edit = workspace.canEdit;
  const board = workspace.boards.find((b) => b.id === task.boardId);
  const columns = [...(board?.columns ?? [])].sort((a, b) => a.sequence - b.sequence);
  const changes = changesOf(task, draft);
  const dirty = Object.keys(changes).length > 0;
  const set = <K extends keyof Draft>(field: K, value: Draft[K]) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const save = useWorkMutation(projectId, () =>
    patchJson(`${WORK}/tasks/${task.id}`, changes, TaskDetailSchema, csrf, task.version),
  );
  const move = useWorkMutation(projectId, (columnId: string) =>
    postJson(
      `${WORK}/tasks/${task.id}/move`,
      { columnId, beforeTaskId: null },
      TaskSummarySchema,
      csrf,
      { expectedVersion: task.version },
    ),
  );
  const archive = useWorkMutation(
    projectId,
    () =>
      patchJson(
        `${WORK}/tasks/${task.id}`,
        { archived: !task.archived },
        TaskDetailSchema,
        csrf,
        task.version,
      ),
    (saved) => {
      if (saved.archived) onClose();
    },
  );
  const failure = [save, move, archive].find((m) => m.isError)?.error ?? null;
  const members = workspace.members;
  const memberLabel = (id: string) =>
    members.find((m) => m.employeeId === id)?.name ?? 'Former member';

  return (
    <Stack spacing={2.5}>
      {failure !== null && (
        <Alert severity="error" variant="outlined">
          {errorMessage(failure)}
        </Alert>
      )}
      {task.archived && (
        <Alert severity="info" variant="outlined">
          This task is archived: it is not shown on the board.
        </Alert>
      )}
      <TextField
        label="Title"
        value={draft.title}
        disabled={!edit}
        required
        onChange={(event) => {
          set('title', event.target.value);
        }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          select
          label="Status"
          value={task.columnId}
          disabled={!edit || move.isPending}
          sx={{ flex: 1 }}
          onChange={(event) => {
            move.mutate(event.target.value);
          }}
        >
          {columns.map((column) => (
            <MenuItem key={column.id} value={column.id}>
              {column.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Priority"
          value={draft.priority}
          disabled={!edit}
          sx={{ flex: 1 }}
          onChange={(event) => {
            set('priority', event.target.value as TaskPriority);
          }}
        >
          {(Object.keys(PRIORITY_LABELS) as TaskPriority[]).map((priority) => (
            <MenuItem key={priority} value={priority}>
              {PRIORITY_LABELS[priority]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Autocomplete
        multiple
        options={members.map((m) => m.employeeId)}
        value={draft.assigneeIds}
        disabled={!edit}
        getOptionLabel={memberLabel}
        onChange={(_, value) => {
          set('assigneeIds', value);
        }}
        renderInput={(params) => <TextField {...params} label="Assignees" />}
      />
      <Autocomplete
        multiple
        options={workspace.labels.map((l) => l.id)}
        value={draft.labelIds}
        disabled={!edit}
        getOptionLabel={(id) => workspace.labels.find((l) => l.id === id)?.name ?? id}
        onChange={(_, value) => {
          set('labelIds', value);
        }}
        renderInput={(params) => <TextField {...params} label="Labels" />}
      />
      <TextField
        select
        label="Milestone"
        value={draft.milestoneId}
        disabled={!edit}
        onChange={(event) => {
          set('milestoneId', event.target.value);
        }}
      >
        <MenuItem value="">None</MenuItem>
        {workspace.milestones.map((milestone) => (
          <MenuItem key={milestone.id} value={milestone.id}>
            {milestone.name}
            {milestone.dueDate === null ? '' : ` (due ${milestone.dueDate})`}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          type="date"
          label="Start date"
          value={draft.startDate}
          disabled={!edit}
          sx={{ flex: 1 }}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => {
            set('startDate', event.target.value);
          }}
        />
        <TextField
          type="date"
          label="Due date"
          value={draft.dueDate}
          disabled={!edit}
          sx={{ flex: 1 }}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => {
            set('dueDate', event.target.value);
          }}
        />
      </Stack>
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      >
        <TextField
          type="number"
          label="Estimate hours"
          value={draft.hours}
          disabled={!edit}
          slotProps={{ htmlInput: { min: 0 } }}
          sx={{ width: 150, flexShrink: 0 }}
          onChange={(event) => {
            set('hours', event.target.value);
          }}
        />
        <TextField
          type="number"
          label="Minutes"
          value={draft.minutes}
          disabled={!edit}
          slotProps={{ htmlInput: { min: 0, max: 59 } }}
          sx={{ width: 120, flexShrink: 0 }}
          onChange={(event) => {
            set('minutes', event.target.value);
          }}
        />
        <DueDate task={task} today={today} />
      </Stack>
      <TextField
        label="Description"
        value={draft.description}
        disabled={!edit}
        multiline
        minRows={3}
        onChange={(event) => {
          set('description', event.target.value);
        }}
      />
      {edit && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={!dirty || draft.title.trim() === '' || save.isPending}
            onClick={() => {
              save.mutate();
            }}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          <Button
            disabled={!dirty || save.isPending}
            color="inherit"
            onClick={() => {
              setDraft(draftOf(task));
            }}
          >
            Discard
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            color="warning"
            disabled={archive.isPending}
            onClick={() => {
              archive.mutate();
            }}
          >
            {task.archived ? 'Restore' : 'Archive'}
          </Button>
        </Stack>
      )}
      <Typography variant="caption" color="text.secondary">
        Created {task.createdAt.slice(0, 10)}
        {task.createdByName === null ? '' : ` by ${task.createdByName}`}
      </Typography>

      {task.parentTaskId === null && (
        <Subtasks projectId={projectId} task={task} tasks={tasks} canEdit={edit} onOpen={onOpen} />
      )}
      {task.parentTaskId !== null && (
        <Link
          component="button"
          type="button"
          onClick={() => {
            onOpen(task.parentTaskId ?? '');
          }}
          sx={{ alignSelf: 'flex-start' }}
        >
          Open the parent task
        </Link>
      )}
      <Checklist projectId={projectId} task={task} canEdit={edit} />
      <Divider />
      <Comments projectId={projectId} taskId={task.id} members={members} />
      <DocumentsPanel
        recordType="projects.task"
        recordId={task.id}
        canAttach={edit}
        title="Attachments"
      />
      <TaskActivity projectId={projectId} taskId={task.id} />
    </Stack>
  );
}

function Subtasks({
  projectId,
  task,
  tasks,
  canEdit,
  onOpen,
}: {
  projectId: string;
  task: TaskDetailDto;
  tasks: readonly TaskSummaryDto[];
  canEdit: boolean;
  onOpen: (taskId: string) => void;
}) {
  const csrf = useCsrf();
  const [title, setTitle] = useState('');
  const subtasks = tasks.filter((t) => t.parentTaskId === task.id);
  const add = useWorkMutation(
    projectId,
    () =>
      postJson(
        `${WORK}/projects/${projectId}/tasks`,
        {
          boardId: task.boardId,
          columnId: task.columnId,
          parentTaskId: task.id,
          title: title.trim(),
        },
        TaskDetailSchema,
        csrf,
      ),
    () => {
      setTitle('');
    },
  );
  return (
    <Box component="section" aria-label="Subtasks">
      <Typography variant="subtitle2" component="h3">
        Subtasks ({subtasks.filter((s) => s.category === 'done').length}/{subtasks.length})
      </Typography>
      <List dense disablePadding>
        {subtasks.map((subtask) => (
          <ListItemButton
            key={subtask.id}
            onClick={() => {
              onOpen(subtask.id);
            }}
          >
            <ListItemText
              primary={`${subtask.key} ${subtask.title}`}
              secondary={subtask.category === 'done' ? 'Done' : undefined}
              slotProps={{
                primary: {
                  sx: { textDecoration: subtask.category === 'done' ? 'line-through' : 'none' },
                },
              }}
            />
            <PriorityChip priority={subtask.priority} />
          </ListItemButton>
        ))}
      </List>
      {canEdit && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="New subtask"
            value={title}
            sx={{ flex: 1 }}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && title.trim() !== '') {
                event.preventDefault();
                add.mutate();
              }
            }}
          />
          <Button
            disabled={title.trim() === '' || add.isPending}
            onClick={() => {
              add.mutate();
            }}
          >
            Add subtask
          </Button>
        </Stack>
      )}
      {add.isError && (
        <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
          {errorMessage(add.error)}
        </Alert>
      )}
    </Box>
  );
}

function Checklist({
  projectId,
  task,
  canEdit,
}: {
  projectId: string;
  task: TaskDetailDto;
  canEdit: boolean;
}) {
  const csrf = useCsrf();
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const items = [...task.checklistItems].sort((a, b) => a.sequence - b.sequence);
  const add = useWorkMutation(
    projectId,
    () =>
      postJson(
        `${WORK}/tasks/${task.id}/checklist`,
        { text: text.trim() },
        ChecklistItemSchema,
        csrf,
      ),
    () => {
      setText('');
    },
  );
  const change = useWorkMutation(
    projectId,
    ({ id, body }: { id: string; body: { text?: string; done?: boolean } }) =>
      patchJson(`${WORK}/checklist-items/${id}`, body, ChecklistItemSchema, csrf),
    () => {
      setEditing(null);
    },
  );
  const remove = useWorkMutation(projectId, (id: string) =>
    deleteNoContent(`${WORK}/checklist-items/${id}`, csrf),
  );
  const failure = [add, change, remove].find((m) => m.isError)?.error ?? null;
  return (
    <Box component="section" aria-label="Checklist">
      <Typography variant="subtitle2" component="h3">
        Checklist ({items.filter((i) => i.done).length}/{items.length})
      </Typography>
      {failure !== null && (
        <Alert severity="error" variant="outlined" sx={{ my: 1 }}>
          {errorMessage(failure)}
        </Alert>
      )}
      <Stack spacing={0.25}>
        {items.map((item) =>
          editing?.id === item.id ? (
            <Stack key={item.id} direction="row" spacing={1}>
              <TextField
                size="small"
                autoFocus
                label="Checklist item"
                value={editing.text}
                sx={{ flex: 1 }}
                onChange={(event) => {
                  setEditing({ id: item.id, text: event.target.value });
                }}
              />
              <Button
                disabled={editing.text.trim() === ''}
                onClick={() => {
                  change.mutate({ id: item.id, body: { text: editing.text.trim() } });
                }}
              >
                Save
              </Button>
              <Button
                color="inherit"
                onClick={() => {
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
            </Stack>
          ) : (
            <Stack key={item.id} direction="row" sx={{ alignItems: 'center' }}>
              <Checkbox
                checked={item.done}
                disabled={!canEdit}
                slotProps={{ input: { 'aria-label': item.text } }}
                onChange={(event) => {
                  change.mutate({ id: item.id, body: { done: event.target.checked } });
                }}
              />
              <Typography
                variant="body2"
                sx={{ flex: 1, textDecoration: item.done ? 'line-through' : 'none' }}
              >
                {item.text}
              </Typography>
              {canEdit && (
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      setEditing({ id: item.id, text: item.text });
                    }}
                  >
                    Edit
                  </Button>
                  <IconButton
                    size="small"
                    aria-label={`Delete ${item.text}`}
                    onClick={() => {
                      remove.mutate(item.id);
                    }}
                  >
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          ),
        )}
      </Stack>
      {canEdit && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="New checklist item"
            value={text}
            sx={{ flex: 1 }}
            onChange={(event) => {
              setText(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && text.trim() !== '') {
                event.preventDefault();
                add.mutate();
              }
            }}
          />
          <Button
            disabled={text.trim() === '' || add.isPending}
            onClick={() => {
              add.mutate();
            }}
          >
            Add item
          </Button>
        </Stack>
      )}
    </Box>
  );
}

/** The partial `@name` being typed at the end of the text, if any. */
export function mentionQuery(text: string): string | null {
  const match = /(?:^|\s)@([^\s@]*)$/.exec(text);
  return match === null ? null : (match[1] ?? '');
}

/** The members whose `@Name` is still in the text when it is sent. */
export function mentionedIn(
  text: string,
  members: readonly ProjectMemberDto[],
  picked: ReadonlySet<string>,
) {
  return members
    .filter((m) => picked.has(m.employeeId) && text.includes(`@${m.name}`))
    .map((m) => m.employeeId);
}

function Comments({
  projectId,
  taskId,
  members,
}: {
  projectId: string;
  taskId: string;
  members: readonly ProjectMemberDto[];
}) {
  const session = useSession();
  const csrf = useCsrf();
  const [body, setBody] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const comments = useQuery({
    queryKey: workKeys.comments(taskId),
    queryFn: () => getData(`${WORK}/tasks/${taskId}/comments`, z.array(TaskCommentSchema)),
  });
  const send = useWorkMutation(
    projectId,
    () =>
      postJson(
        `${WORK}/tasks/${taskId}/comments`,
        { body: body.trim(), mentionEmployeeIds: mentionedIn(body, members, picked) },
        TaskCommentSchema,
        csrf,
      ),
    () => {
      setBody('');
      setPicked(new Set());
      void comments.refetch();
    },
  );
  const change = useWorkMutation(
    projectId,
    ({ id, text }: { id: string; text: string }) =>
      patchJson(`${WORK}/comments/${id}`, { body: text }, TaskCommentSchema, csrf),
    () => {
      setEditing(null);
      void comments.refetch();
    },
  );
  const query = mentionQuery(body);
  const suggestions =
    query === null
      ? []
      : members.filter((m) => m.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6);
  const pick = (member: ProjectMemberDto) => {
    setBody((current) => current.replace(/@([^\s@]*)$/, `@${member.name} `));
    setPicked((current) => new Set(current).add(member.employeeId));
  };
  const failure = [send, change].find((m) => m.isError)?.error ?? null;
  return (
    <Box component="section" aria-label="Comments">
      <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
        Comments
      </Typography>
      {comments.isError && (
        <Alert severity="error" variant="outlined">
          {errorMessage(comments.error)}
        </Alert>
      )}
      <Stack spacing={1.25}>
        {(comments.data ?? []).map((comment) => (
          <Paper key={comment.id} variant="outlined" sx={{ p: 1.25 }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <PersonName name={comment.authorName} userId={comment.authorUserId} size={22} />
              <Typography variant="caption" color="text.secondary">
                {comment.createdAt.slice(0, 16).replace('T', ' ')}
                {comment.editedAt === null ? '' : ' (edited)'}
              </Typography>
            </Stack>
            {editing?.id === comment.id ? (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <TextField
                  multiline
                  minRows={2}
                  label="Edit comment"
                  value={editing.body}
                  onChange={(event) => {
                    setEditing({ id: comment.id, body: event.target.value });
                  }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={editing.body.trim() === '' || change.isPending}
                    onClick={() => {
                      change.mutate({ id: comment.id, text: editing.body.trim() });
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      setEditing(null);
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.75, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {comment.body}
                </Typography>
                {comment.authorUserId === session.data?.userId && (
                  <Button
                    size="small"
                    sx={{ mt: 0.5 }}
                    onClick={() => {
                      setEditing({ id: comment.id, body: comment.body });
                    }}
                  >
                    Edit
                  </Button>
                )}
              </>
            )}
          </Paper>
        ))}
      </Stack>
      {failure !== null && (
        <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
          {errorMessage(failure)}
        </Alert>
      )}
      <Box sx={{ position: 'relative', mt: 1.5 }}>
        <TextField
          multiline
          minRows={2}
          fullWidth
          label="Add a comment"
          helperText="Type @ to mention a project member: they are notified."
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
          }}
        />
        {suggestions.length > 0 && (
          <Paper
            elevation={4}
            sx={{ position: 'absolute', zIndex: 2, left: 0, right: 0, mt: -2.5 }}
          >
            <MenuList aria-label="Mention a member" dense>
              {suggestions.map((member) => (
                <MenuItem
                  key={member.employeeId}
                  onClick={() => {
                    pick(member);
                  }}
                >
                  {member.name}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        )}
      </Box>
      <Button
        variant="outlined"
        sx={{ mt: 1 }}
        disabled={body.trim() === '' || send.isPending}
        onClick={() => {
          send.mutate();
        }}
      >
        {send.isPending ? 'Sending…' : 'Comment'}
      </Button>
    </Box>
  );
}

function TaskActivity({ projectId, taskId }: { projectId: string; taskId: string }) {
  const activity = useActivity(projectId, taskId);
  if (activity.data === undefined || activity.data.length === 0) return null;
  return (
    <Box component="section" aria-label="Task activity">
      <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
        Activity
      </Typography>
      <Stack spacing={0.5}>
        {activity.data.map((entry) => (
          <Typography key={entry.id} variant="caption" color="text.secondary">
            {entry.occurredAt.slice(0, 16).replace('T', ' ')} · {entry.actorName ?? 'System'} ·{' '}
            {entry.summary}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}
